// Pure chip-spec + urgency-color helpers for the Review Purchase List product
// cell. Thresholds mirror the buying table (buyingTableColors / Atlas
// urgencyTokens) so a Warbler user sees consistent colors. Vuetify color names:
// error=red, warning=orange, success=green, info=blue, grey=neutral.
import type { ReviewInventorySignals } from '@/utils/purchase-order/inventoryReportRowMapper';

export const URGENCY_CRITICAL_DAYS = 7;
export const URGENCY_WARNING_DAYS = 14;

/** Stock chip color: oversold/zero → red; positive → green. */
export function stockChipColor(stock: number): string {
  return stock <= 0 ? 'error' : 'success';
}

/** Days-left chip color (uses the RAW value, so negatives are critical). */
export function daysLeftChipColor(days: number): string {
  if (days <= URGENCY_CRITICAL_DAYS) { return 'error'; }
  if (days <= URGENCY_WARNING_DAYS) { return 'warning'; }
  return 'grey';
}

export interface ReviewChipSpec {
  key: 'stock' | 'days' | 'incoming' | 'lead';
  label: string;
  color: string;
  /** Vuetify text-color (for filled chips). Omitted for the muted lead chip. */
  textColor?: string;
}

const MINUS = '−'; // U+2212 renders cleanly for negative stock
function signed(n: number): string {
  return n < 0 ? `${MINUS}${Math.abs(n)}` : String(n);
}

/**
 * Chips to render for one variant's inventory signals. A `null` signal hides
 * its chip; `0` renders (the "we know, and it's zero" case). Incoming only
 * renders when > 0. Order: stock → days → incoming → lead.
 */
export function reviewChips(inv: ReviewInventorySignals | null | undefined): ReviewChipSpec[] {
  if (!inv) { return []; }
  const chips: ReviewChipSpec[] = [];

  if (inv.stock != null) {
    chips.push({
      key: 'stock',
      label: `Stock ${signed(inv.stock)}`,
      color: stockChipColor(inv.stock),
      textColor: 'white',
    });
  }
  if (inv.daysLeft != null) {
    const d = Math.max(0, Math.round(inv.daysLeft));
    chips.push({
      key: 'days',
      label: `${d} day${d === 1 ? '' : 's'} left`,
      color: daysLeftChipColor(inv.daysLeft),
      textColor: 'white',
    });
  }
  if (inv.incoming != null && inv.incoming > 0) {
    chips.push({ key: 'incoming', label: `Incoming ${inv.incoming}`, color: 'info', textColor: 'white' });
  }
  if (inv.leadTime != null) {
    chips.push({ key: 'lead', label: `${inv.leadTime}d lead`, color: 'grey lighten-3' });
  }

  return chips;
}
